import {
  ArdoqId,
  FilterAttributes,
  FilterTypes,
  QueryBuilderSubquery,
} from '@ardoq/api-types';
import { DynamicFilterValues, SpecialFilterKeys } from './filterEnums';
import {
  isPureTypeEqualitySubquery,
  QuickPerspectiveTypeEqualitySubquery,
} from './predicates';
import {
  ComponentLabelSource,
  LabelFormattingInfo,
  ReferenceLabelSource,
} from '@ardoq/data-model';
import { isNil, isString } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

type Result = boolean;

export const isResult = (
  filterValueOrResult: ArdoqId | Result
): filterValueOrResult is Result => {
  return typeof filterValueOrResult === 'boolean';
};

type ParseFilterValueArgs = {
  filterValue: ArdoqId | DynamicFilterValues;
  currentComponentId?: ArdoqId;
  testValue?: ArdoqId;
};

/**
 * Parse a filterValue to a ComponentId, or short circuit to a Result
 *
 * Will parse special values and short-circuit to a Result, or return the
 * ComponentId that should be matched.
 */

export const parseFilterValue = ({
  filterValue,
  currentComponentId,
  testValue,
}: ParseFilterValueArgs): ArdoqId | Result => {
  switch (filterValue) {
    case DynamicFilterValues.COMPONENT_CURRENT:
      if (!currentComponentId) return true as Result;

      return currentComponentId;
    case DynamicFilterValues.COMPONENT_PARENT_NONE:
      return !testValue as Result;
    default:
      return filterValue as ArdoqId;
  }
};

const SpecialAttributeTypeValues: string[] = Object.values(SpecialFilterKeys);

export const isSpecialAttributeType = (
  filterAttributeName: string
): filterAttributeName is SpecialFilterKeys =>
  SpecialAttributeTypeValues.includes(filterAttributeName);

type FindQuickPerspectivesTypeEqualitySubquery = (
  ruleRoot: QueryBuilderSubquery
) => QuickPerspectiveTypeEqualitySubquery | null;
/**
 * The type equality rules should always be combined with each other with an OR condition, otherwise the filter
 * cannot be truthy for anything. But the top level root condition can be either 'AND' or 'OR'.
 *
 * Why?
 *
 * We have two different logical compositions for QP type equality rules:
 * 1. if there are no other rules defined in the root, we display either of the selected types
 * 2. if there are other rules defined in the root, we display the intersection of the user-defined rules with
 *    either of the selected types
 * That means:
 * 1. if there are no other rules defined in the root, we can safley place the type equality rules directly in
 *    the root with the 'OR' condition
 * 2. if there are other rules defined in the root, we should place / (look for) the type equality rules in a
 *    nested subquery, where the subquery condition is OR, and the top level root condition (that combines user-
 *    defined rules with QP rules) is AND
 *
 * It is important to note that
 * - we should add all type rules to the same subquery because nothing can be of two types at the same time (in
 *   case of root level AND condition).
 * - the QP subquery should sit in the rules of the root query or be the root query itself, not deeper.
 * - this doc is also valid for `clearQucikPerspectiveTypeFilters`
 *
 * Data structure example with user-defined rules:
 * ```ts
 * const rootQuery = {
 *   condition: 'AND', // must be AND
 *   rules: [
 *     {
 *       condition: 'AND', // could be OR - does not matter as long it's a subquery
 *       rules: [
 *         { id: 'name',
 *           field: 'name',
 *           input: 'text',
 *           type: 'string',
 *           operator: Operator.CONTAINS_SUBSTRING,
 *           value: 'e',
 *         },
 *         {
 *           id: 'description',
 *           field: 'description',
 *           input: 'text',
 *           type: 'string',
 *           operator: Operator.CONTAINS_SUBSTRING,
 *           value: 'hello',
 *         },
 *       ],
 *     },
 *     {
 *       condition: 'OR', // must be OR
 *       rules: [
 *         { field: 'type', operator: 'equal', value: 'Application' },
 *         { field: 'type', operator: 'equal', value: 'Interface'   },
 *       ],
 *     },
 *   ],
 * }
 * ```
 *
 * Data structure example without user-defined rules:
 * ```ts
 * const rootQuery = {
 *   condition: 'OR', // must be OR
 *   rules: [
 *     { field: 'type', operator: 'equal', value: 'Application' },
 *     { field: 'type', operator: 'equal', value: 'Interface'   },
 *   ],
 * }
 * ```
 */
export const findQuickPerspectivesTypeEqualitySubquery: FindQuickPerspectivesTypeEqualitySubquery =
  ruleRoot =>
    isPureTypeEqualitySubquery(ruleRoot)
      ? ruleRoot
      : (ruleRoot.rules.find(isPureTypeEqualitySubquery) ?? null);

export const getLabelFormattingRules = (
  conditionalFormatting: FilterAttributes[]
) => getLabelFormattingFromFilterAttributes(conditionalFormatting);

const FILTERS_TO_EXCLUDE_FOR_MULTI_LABEL: string[] = [
  ReferenceLabelSource.NONE,
  ReferenceLabelSource.REFERENCE_TYPE,
  ComponentLabelSource.NONE,
  ComponentLabelSource.NAME,
];
const shouldExcludeFilterForMultiLabel = (filter: FilterAttributes) =>
  FILTERS_TO_EXCLUDE_FOR_MULTI_LABEL.includes(filter.value as string);

type ComposeLabelFormattingRuleKeyArgs = {
  filterType: FilterTypes;
  affectsTypeName: string | null;
  showFieldName: boolean;
};
export const composeLabelFormattingRuleKey = ({
  filterType,
  affectsTypeName,
  showFieldName,
}: ComposeLabelFormattingRuleKeyArgs) =>
  `${filterType}-${affectsTypeName ?? 'allTypes'}-${showFieldName}`;

const composeTypeNameKey = (type: string, typeName: string | null): string =>
  `${type}-${typeName ?? 'null'}`;

const isFormattingForAllTypes = (labelFormattingInfo: LabelFormattingInfo) =>
  labelFormattingInfo.typeName === null;

export const reduceLabelFormatting = (
  result: Map<string, LabelFormattingInfo>,
  filter: FilterAttributes
) => {
  const filterType = filter.type;
  const isComponentLabelFormattingFilter =
    filterType === FilterTypes.COMPONENT_LABEL;
  const isReferenceLabelFormattingFilter =
    filterType === FilterTypes.REFERENCE_LABEL;
  const isLabelFormattingFilter =
    isComponentLabelFormattingFilter || isReferenceLabelFormattingFilter;
  if (!isLabelFormattingFilter || shouldExcludeFilterForMultiLabel(filter)) {
    return result;
  }
  const affectsTypeName = filter.affectsTypeName || null;
  const fieldName = filter.value;
  if (!isString(fieldName)) {
    return result;
  }

  // if there is no affectsTypeName, we are dealing with a global label formatting, or possibly the old label formatting
  // in slides. In this case we want to display the field name if there is one
  const showFieldName = Boolean(filter.showFieldName) || isNil(affectsTypeName);

  const key = composeLabelFormattingRuleKey({
    filterType: filterType,
    affectsTypeName: affectsTypeName,
    showFieldName: showFieldName,
  });
  const existingLabelFormatting = result.get(key);
  if (existingLabelFormatting) {
    const { fields } = existingLabelFormatting;
    if (!fields.includes(fieldName)) {
      fields.push(fieldName);
    }
  } else {
    result.set(key, {
      typeName: affectsTypeName,
      type: isComponentLabelFormattingFilter ? 'component' : 'reference',
      fields: [fieldName],
      showFieldName,
      __id: uuidv4(),
    });
  }
  return result;
};

/**
 * Removes fields from formatting rules with specific types if
 *  - they are already present in the component/reference "all types" rules
 *  - they are present in same type formatting rules with showFieldName
 *
 *  Removes rules if all fields are removed
 */
export const filterLabelFormattingMap = (
  labelFormattingMap: Map<string, LabelFormattingInfo>
) => {
  const allTypeFields = {
    component: new Set<string>(),
    reference: new Set<string>(),
  };

  const fieldsWithShowNameByType = new Map<string, Set<string>>();

  labelFormattingMap.forEach(labelFormatting => {
    const { type, typeName, showFieldName, fields } = labelFormatting;

    if (isFormattingForAllTypes(labelFormatting)) {
      fields.forEach(field => allTypeFields[type].add(field));
      return;
    }

    if (showFieldName) {
      const typeKey = composeTypeNameKey(type, typeName);
      const fieldSet =
        fieldsWithShowNameByType.get(typeKey) || new Set<string>();
      fields.forEach(field => fieldSet.add(field));
      fieldsWithShowNameByType.set(typeKey, fieldSet);
    }
  });

  const result = new Map<string, LabelFormattingInfo>();

  labelFormattingMap.forEach((labelFormatting, key) => {
    const { type, typeName, showFieldName, fields } = labelFormatting;

    const filteredFields = fields.filter(field => {
      if (
        !isFormattingForAllTypes(labelFormatting) &&
        allTypeFields[type].has(field)
      ) {
        return false;
      }

      if (!showFieldName) {
        const typeKey = composeTypeNameKey(type, typeName);
        const fieldsWithShowName = fieldsWithShowNameByType.get(typeKey);
        if (fieldsWithShowName?.has(field)) {
          return false;
        }
      }

      return true;
    });

    if (filteredFields.length > 0) {
      result.set(key, { ...labelFormatting, fields: filteredFields });
    }
  });

  return result;
};

export const getLabelFormattingFromFilterAttributes = (
  filterAttributes: FilterAttributes[]
) => {
  const labelFormattingMap: Map<string, LabelFormattingInfo> =
    filterAttributes.reduce(reduceLabelFormatting, new Map());

  const filteredLabelFormattingMap =
    filterLabelFormattingMap(labelFormattingMap);

  return Array.from(filteredLabelFormattingMap.values());
};

export const getAttributeFormattingValue = <T>(
  isFormattingFilter: boolean,
  filterValue: unknown,
  filterType: string | undefined,
  predicate: (value: unknown) => value is T
): T | null => {
  if (!isFormattingFilter) return null;

  const isObjectAttrFormatting =
    filterValue &&
    typeof filterValue === 'object' &&
    !Array.isArray(filterValue) &&
    filterType === FilterTypes.ATTRIBUTE;

  if (!isObjectAttrFormatting) return null;

  return predicate(filterValue) ? filterValue : null;
};
